import PropTypes from "prop-types"
import React, { useEffect, useState, useMemo } from "react"
import { Link } from "react-router-dom"
import { withTranslation } from "react-i18next"
import { Alert, Modal, ModalBody, Spinner } from "reactstrap"
import { toast } from "react-toastify"
import moment from "moment"

// Components
import Breadcrumbs from "../../components/Common/Breadcrumb2"
import CampaignPermissionNotice from "components/Common/CampaignPermissionNotice"
import TableContainer from "../../components/Common/TableContainer2"
import MyTooltip from "components/MyTooltip"

// Helpers
import { del } from "helpers/api_helper"
import { getAllPages } from "helpers/api_pagination_helper"
import { getApiErrorMessage } from "helpers/api_error_helper"
import useCurrentPermissions from "hooks/useCurrentPermissions"
import "../../components/Common/datatables.scss"

const DeleteCampaignModal = ({ show, onDeleteClick, onCloseClick, isDeleting }) => {
  return (
    <Modal size="md" isOpen={show} toggle={onCloseClick} centered={true}>
      <ModalBody className="px-4 py-4">
        <h5 className="mb-4" style={{ fontWeight: "600" }}>Are you sure to delete?</h5>
        
        <div className="d-flex gap-2 justify-content-end mt-4">
          <button
            type="button"
            className="btn btn-outline-secondary"
            style={{ 
              minWidth: "100px", 
              backgroundColor: "#fff", 
              border: "1px solid #ced4da",
              color: "#495057",
              borderRadius: "8px"
            }}
            onClick={onCloseClick}
            disabled={isDeleting}
          >
            No
          </button>
          <button
            type="button"
            className="btn btn-danger"
            style={{ 
              minWidth: "100px",
              borderRadius: "8px"
            }}
            onClick={onDeleteClick}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Yes"}
          </button>
        </div>
      </ModalBody>
    </Modal>
  )
}

const SurveyLineCampaignList = props => {
  const [deleteModal, setDeleteModal] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [contentList, setContentList] = useState([])
  const [contentListLoading, setContentListLoading] = useState(true)
  const [loadError, setLoadError] = useState("")
  const [isDeleting, setIsDeleting] = useState(false)
  const permissions = useCurrentPermissions()
  const canCreate = permissions.hasScope("survey-line-campaign:create")
  const canUpdate = permissions.hasScope("survey-line-campaign:update")
  const canDelete = permissions.hasScope("survey-line-campaign:delete")

  // Badge style helper
  const badgeStyle = (bgColor, textColor, borderColor) => ({
    backgroundColor: bgColor,
    color: textColor,
    border: `1px solid ${borderColor}`,
    padding: "4px 16px",
    borderRadius: "20px",
    fontWeight: "600",
    fontSize: "13px",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px"
  })

  // Campaign Status Badge
  const getCampaignStatusBadge = (status) => {
    if (!status) return <span className="badge" style={badgeStyle("#f5f5f5", "#616161", "#e0e0e0")}>-</span>
    
    const normalizedStatus = status.toLowerCase()
    
    switch (normalizedStatus) {
      case "draft":
        return <span className="badge" style={badgeStyle("#f5f5f5", "#595959", "#d9d9d9")}>Draft</span>
      case "schedule":
        return <span className="badge" style={badgeStyle("#eef6ff", "#2563eb", "#d1e5ff")}>Schedule</span>
      case "publish":
        return <span className="badge" style={badgeStyle("#e8f5e9", "#2e7d32", "#c8e6c9")}>Publish</span>
      case "failed":
        return <span className="badge" style={badgeStyle("#ffebee", "#c62828", "#ef9a9a")}>Failed</span>
      default:
        return <span className="badge" style={badgeStyle("#f5f5f5", "#616161", "#bdbdbd")}>{status}</span>
    }
  }


  const handleDeleteCampaign = async () => {
    if (!selectedId || isDeleting || !canDelete) return
    setIsDeleting(true)
    try {
      await del(
        process.env.REACT_APP_BACKEND_URL + "/v1/admin/line-campaign/" + selectedId
      )
      setDeleteModal(false)
      toast.success("Campaign deleted successfully")
      setContentList(prevList => prevList.filter(item => item.id !== selectedId))
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error deleting campaign."))
    } finally {
      setIsDeleting(false)
    }
  }

  const columns = useMemo(() => [
    {
      header: "#",
      enableColumnFilter: false,
      enableSorting: true,
      cell: cellProps => {
        return (
          <div style={{ display: "flex", justifyContent: "center" }}>
            {parseInt(cellProps.row.id) + 1}
          </div>
        )
      },
    },
    {
      header: "Campaign Name",
      accessorKey: "campaign_name",
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Start Date",
      accessorKey: "start_date",
      enableColumnFilter: false,
      enableSorting: true,
      cell: cellProps => {
        return cellProps.row.original.start_date
          ? moment(cellProps.row.original.start_date).format("YYYY-MM-DD")
          : "-"
      },
    },
    {
      header: "End Date",
      accessorKey: "end_date",
      enableColumnFilter: false,
      enableSorting: true,
      cell: cellProps => {
        return cellProps.row.original.end_date
          ? moment(cellProps.row.original.end_date).format("YYYY-MM-DD")
          : "-"
      },
    },
    {
      header: "Campaign Status",
      accessorKey: "status",
      enableColumnFilter: false,
      enableSorting: true,
      cell: cellProps => {
        return getCampaignStatusBadge(cellProps.row.original.status)
      },
    },
    {
      header: "Created Date",
      accessorKey: "created_at",
      enableColumnFilter: false,
      enableSorting: true,
      cell: cellProps => {
        return cellProps.row.original.created_at
          ? moment(cellProps.row.original.created_at).format("YYYY-MM-DD")
          : "-"
      },
    },
    {
      header: "Tools",
      accessorKey: "tools",
      enableColumnFilter: false,
      enableSorting: false,
      cell: cellProps => {
        let _id = cellProps.row.original.id
        const status = (cellProps.row.original.status || "").toLowerCase()
        const canEdit = ["draft", "failed"].includes(status)
        const btnStyle = {
          width: "32px",
          height: "32px",
          padding: "0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }
        return (
          <div className="d-flex gap-1">
            <MyTooltip label="View">
              <Link
                className="btn btn-sm btn-info"
                style={btnStyle}
                to={`/survey-line-campaign/view/${_id}`}
              >
                <i className="bx bx-show"></i>
              </Link>
            </MyTooltip>

            <MyTooltip label="History">
              <Link
                className="btn btn-sm btn-secondary"
                style={btnStyle}
                to={`/survey-line-campaign/history/${_id}`}
              >
                <i className="bx bx-history"></i>
              </Link>
            </MyTooltip>

            {canEdit && canUpdate && (
              <MyTooltip label="Edit">
                <Link
                  className="btn btn-sm btn-warning"
                  style={btnStyle}
                  to={`/survey-line-campaign/edit/${_id}`}
                >
                  <i className="bx bx-edit-alt"></i>
                </Link>
              </MyTooltip>
            )}

            {canDelete && <button
              aria-label="Delete campaign"
              className="btn btn-sm btn-danger"
              style={btnStyle}
              onClick={() => {
                setSelectedId(_id)
                setDeleteModal(true)
              }}
            >
              <i className="bx bx-trash"></i>
            </button>}
          </div>
        )
      },
    },
  ], [canUpdate, canDelete])

  useEffect(() => {
    setContentListLoading(true)
    const fetchContentList = async () => {
      try {
        const result = await getAllPages(
          process.env.REACT_APP_BACKEND_URL + "/v1/admin/line-campaign"
        )
        setContentList(result)
      } catch (error) {
        setLoadError(getApiErrorMessage(error, "Unable to load LINE campaigns."))
      }
      setContentListLoading(false)
    }

    fetchContentList()
  }, [])

  //meta title
  document.title = "Survey Line Campaign | " + process.env.REACT_APP_CMS_TITLE

  return (
    <>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs
            title="Survey Line Campaign"
            breadcrumbItems={[
              { title: "Survey Line Campaign", link: "survey-line-campaign" },
              { title: "List", link: "list" },
            ]}
          />

          <div className="bInnerContent">
            {loadError && <Alert color="danger">{loadError}</Alert>}
            <CampaignPermissionNotice permissions={permissions} />
            <div className="d-flex flex-column align-items-end mb-2">
              {canCreate ? (
                <Link to="/survey-line-campaign/create" className="btn btn-primary">
                  <i className="bx bx-plus me-1"></i> Create
                </Link>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled
                  aria-describedby={!permissions.loading && !permissions.error ? "campaign-create-permission-reason" : undefined}
                >
                  <i className="bx bx-plus me-1"></i> Create
                </button>
              )}
              {!permissions.loading && !permissions.error && !canCreate && (
                <small id="campaign-create-permission-reason" className="text-muted mt-1">
                  Your account does not have permission to create campaigns.
                </small>
              )}
            </div>
            {contentListLoading ? (
              <Spinner color="primary" />
            ) : !loadError && (
              <TableContainer
                isAddButton={false}
                columns={columns}
                data={contentList || []}
                isGlobalFilter={true}
                isPagination={true}
                SearchPlaceholder="Search"
                pagination="pagination"
                paginationWrapper="dataTables_paginate paging_simple_numbers"
                tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                rootMenu="survey-line-campaign"
                isExportToExcel={false}
              />
            )}
          </div>
        </div>
      </div>

      <DeleteCampaignModal
        show={deleteModal}
        onDeleteClick={handleDeleteCampaign}
        onCloseClick={() => setDeleteModal(false)}
        isDeleting={isDeleting || !canDelete}
      />
    </>
  )
}

SurveyLineCampaignList.propTypes = {
  t: PropTypes.any,
}

export default withTranslation()(SurveyLineCampaignList)
